import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import LoginPage from "@/pages/LoginPage";
import WorkoutMonitorV2 from "@/pages/WorkoutMonitorV2";
import ProfileSetup from "@/pages/ProfileSetup";
import WorkoutHistory from "@/pages/WorkoutHistory";
import DeviceConnection from "@/pages/DeviceConnection";
import TrainingPrograms from "@/pages/TrainingPrograms";
import ProgramMonitor from "@/pages/ProgramMonitor";
import Coxswain from "@/pages/Coxswain";
import TrainingSessionAnalysis from "@/pages/TrainingSessionAnalysis";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { WorkoutProvider } from "@/contexts/WorkoutContext";
import { StrokeDetectionProvider } from "@/contexts/StrokeDetectionContext";
import SplashScreen from "@/components/SplashScreen";
import InstallPrompt from "@/components/InstallPrompt";
import OfflineStatus from "@/components/OfflineStatus";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AuthCallback() {
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [authStatus, setAuthStatus] = useState('processing');
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    const params = new URLSearchParams(hash.substring(1));
    const sessionId = params.get('session_id');
    const error = params.get('error');

    console.log('Auth callback - sessionId:', sessionId ? 'present' : 'missing', 'error:', error);

    if (error) {
      console.error('Auth error from provider:', error);
      setAuthStatus('error');
      
      // Provide helpful error messages for common issues
      let errorMessage = `Auth provider error: ${error}`;
      if (error.includes('disallowed_useragent') || error.includes('403')) {
        errorMessage = 'Please open this app directly in Chrome or Safari. In-app browsers (from WhatsApp, Facebook, etc.) are not supported by Google Sign-In.';
      } else if (error.includes('access_denied')) {
        errorMessage = 'Sign-in was cancelled. Please try again.';
      }
      
      setAuthError(errorMessage);
      setTimeout(() => {
        window.history.replaceState({}, document.title, '/');
        window.location.href = '/';
      }, 5000); // Give more time to read the error
      return;
    }

    if (!sessionId) {
      console.log('No session_id in callback, redirecting to login');
      setAuthStatus('error');
      setAuthError('No session ID received');
      setTimeout(() => {
        window.history.replaceState({}, document.title, '/');
        window.location.href = '/';
      }, 2000);
      return;
    }

    // Set a timeout in case the fetch hangs
    const timeoutId = setTimeout(() => {
      console.error('Auth timeout - request took too long');
      setAuthStatus('error');
      setAuthError('Authentication timed out. Please try again.');
    }, 15000);

    // Exchange session_id for session_token
    fetch(`${API}/auth/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ session_id: sessionId })
    })
      .then(res => {
        clearTimeout(timeoutId);
        console.log('Auth response status:', res.status);
        if (!res.ok) {
          return res.json().then(data => {
            // Extract user-friendly error message
            const errorMsg = data.detail || 'Authentication failed';
            throw new Error(errorMsg);
          }).catch(parseErr => {
            // Fallback if JSON parsing fails
            if (parseErr.message && !parseErr.message.includes('JSON')) {
              throw parseErr;
            }
            throw new Error(`Authentication failed (${res.status})`);
          });
        }
        return res.json();
      })
      .then(user => {
        console.log('Auth successful, user:', user.email);
        setAuthStatus('success');

        // Check if profile is complete
        const profileComplete = user.dob && user.sex && user.height && user.weight;

        // Verify the session cookie was actually committed BEFORE navigating.
        // Safari and several mobile WebViews can race Set-Cookie against a
        // window.location.href change, causing the freshly-loaded page to see
        // no cookie → bounce back to /login → rower blames "Google Auth" and
        // retries 2-3 times. Poll /auth/me for up to 5s, then navigate.
        // Route every fresh Google sign-in through the boat-selection
        // screen (LoginPage renders the picker when isAuthenticated is
        // true). Users pick their boat/erg for THIS session, then land
        // on the Monitor. Previously we skipped straight to /dashboard
        // for returning users with a saved `default_boat_type`, but the
        // product intent is that every login lets the rower reconfirm
        // the boat for today's session.
        const nextPath = !profileComplete ? '/profile-setup' : '/';

        const waitForCookie = async () => {
          const deadline = Date.now() + 5000;
          while (Date.now() < deadline) {
            try {
              const r = await fetch(`${API}/auth/me`, { credentials: 'include' });
              if (r.ok) return true;
            } catch (_e) { /* retry */ }
            await new Promise((res) => setTimeout(res, 250));
          }
          return false;
        };

        (async () => {
          await waitForCookie();
          window.location.href = nextPath;
        })();
      })
      .catch(err => {
        clearTimeout(timeoutId);
        console.error('Auth error:', err);
        setAuthStatus('error');
        setAuthError(err.message || 'Authentication failed');
        // Clear the hash to prevent loop
        window.history.replaceState({}, document.title, '/');
      });
  }, [location]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-900">
      <div className="text-center p-6">
        {authStatus === 'processing' && (
          <>
            <div className="w-16 h-16 border-4 border-zinc-900 dark:border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-sm font-semibold uppercase tracking-wider text-zinc-900 dark:text-white">Authenticating...</p>
          </>
        )}
        {authStatus === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm font-semibold uppercase tracking-wider text-green-600 dark:text-green-400">Success! Redirecting...</p>
          </>
        )}
        {authStatus === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-sm font-semibold uppercase tracking-wider text-red-600 dark:text-red-400 mb-2">Authentication Failed</p>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-4 max-w-xs mx-auto">{authError}</p>
            <div className="flex gap-3 justify-center">
              <button 
                onClick={() => {
                  // Clear any cached auth state and redirect to login
                  localStorage.removeItem('session_token');
                  window.history.replaceState({}, document.title, '/');
                  window.location.href = '/';
                }}
                className="px-4 py-2 bg-zinc-200 dark:bg-zinc-700 text-zinc-900 dark:text-white rounded-lg text-sm font-bold"
              >
                Back to Login
              </button>
              <button 
                onClick={() => {
                  // Retry the login flow
                  const redirectUrl = window.location.origin + '/';
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                className="px-4 py-2 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-lg text-sm font-bold"
              >
                Try Again
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const CACHED_USER_KEY = 'crewz_cached_user';

function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const hasChecked = useRef(false);

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    if (hasChecked.current) return;
    hasChecked.current = true;

    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          console.error('Auth check failed:', response.status);
          throw new Error('Not authenticated');
        }
        
        const userData = await response.json();
        try { localStorage.setItem(CACHED_USER_KEY, JSON.stringify(userData)); } catch { /* noop */ }
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        // No signal → trust the last signed-in user so the app still opens offline.
        // A real 401 ('Not authenticated') clears the cached user.
        const cached = (() => { try { return JSON.parse(localStorage.getItem(CACHED_USER_KEY) || 'null'); } catch { return null; } })();
        if (error.message !== 'Not authenticated' && cached) {
          setUser(cached);
          setIsAuthenticated(true);
          return;
        }
        if (error.message === 'Not authenticated') { try { localStorage.removeItem(CACHED_USER_KEY); } catch { /* noop */ } }
        console.error('Auth error:', error);
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, [location]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-zinc-900 dark:border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm font-semibold uppercase tracking-wider text-zinc-900 dark:text-white">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LoginPage />} />
      <Route path="/profile-setup" element={<ProtectedRoute><ProfileSetup /></ProtectedRoute>} />
      {/* Convenience alias — the boat picker lives inside LoginPage's
          authenticated state, so /profile lands there. Prevents blank
          screens for anyone who typed the more common /profile path. */}
      <Route path="/profile" element={<Navigate to="/profile-setup" replace />} />
      <Route path="/dashboard" element={<ProtectedRoute><WorkoutMonitorV2 /></ProtectedRoute>} />
      {/* Monitor lives on /dashboard; /monitor is the spec-familiar alias. */}
      <Route path="/monitor" element={<Navigate to="/dashboard" replace />} />
      <Route path="/history" element={<ProtectedRoute><WorkoutHistory /></ProtectedRoute>} />
      <Route path="/history/:workoutId/analysis" element={<ProtectedRoute><TrainingSessionAnalysis /></ProtectedRoute>} />
      <Route path="/devices" element={<ProtectedRoute><DeviceConnection /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><DeviceConnection /></ProtectedRoute>} />
      <Route path="/programs" element={<ProtectedRoute><TrainingPrograms /></ProtectedRoute>} />
      <Route path="/program-monitor" element={<ProtectedRoute><ProgramMonitor /></ProtectedRoute>} />
      <Route path="/coxswain" element={<ProtectedRoute><Coxswain /></ProtectedRoute>} />
      {/* Catch-all — any unknown path bounces back to the LoginPage
          (which itself renders the boat picker when authenticated).
          Prevents blank white pages from typos or stale bookmarks. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <WorkoutProvider>
        <StrokeDetectionProvider>
        <div className="App min-h-screen bg-white dark:bg-zinc-900 transition-colors">
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster position="top-right" />
          {/* Opening animation — only shows once per browser session
              and never gates the OAuth callback route. */}
          <SplashScreen />
          <InstallPrompt />
          <OfflineStatus />
        </div>
        </StrokeDetectionProvider>
      </WorkoutProvider>
    </ThemeProvider>
  );
}

export default App;